import React from "react";
import { makeStyles } from "@mui/styles";
import { MailProDialog } from "views/Pma/MailPro/components/MailProDialog/MailProDialog";
import { MailProDialogHeader } from "views/Pma/MailPro/components/MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "views/Pma/MailPro/components/MailProDialog/MailProDialogBody";
import MailProButton from "views/Pma/MailPro/components/UI/MailProButton";

export const MAX_TITLE_LENGTH = 50;
export const MAX_SUBTITLE_LENGTH = 120;

const DIALOG_BUTTON_BORDER_RADIUS = "8px";
const DIALOG_BUTTON_MIN_HEIGHT = "30px";

const useStyles = makeStyles({
  dialogPaper: {
    maxWidth: "560px",
    width: "48vw",
    "& .MuiDialogContent-root": { padding: "0 !important" },
  },
  dialogHeader: {
    backgroundColor: "#4A7396",
    color: "white",
    padding: "8px 24px",
    textAlign: "center",
    width: "100%",
  },
  headerTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
  },
  dialogBody: {
    padding: "20px 24px 12px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    color: "#374151",
    fontFamily: "objektiv-mk2, sans-serif",
  },
  input: {
    width: "100%",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    fontSize: "13px",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "left",
    padding: "8px 10px",
    boxSizing: "border-box",
    lineHeight: 1.4,
    "&:focus": {
      outline: "none",
      borderColor: "#4A7396",
    },
    "&:disabled": {
      backgroundColor: "#f3f4f6",
      color: "#9ca3af",
      cursor: "not-allowed",
    },
  },
  textarea: {
    width: "100%",
    minHeight: "96px",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    fontSize: "13px",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "left",
    padding: "8px 10px",
    boxSizing: "border-box",
    resize: "vertical",
    lineHeight: 1.4,
    "&:focus": {
      outline: "none",
      borderColor: "#4A7396",
    },
    "&:disabled": {
      backgroundColor: "#f3f4f6",
      color: "#9ca3af",
      cursor: "not-allowed",
    },
  },
  fieldError: {
    borderColor: "#c62828",
    backgroundColor: "#fef2f2",
  },
  counter: {
    fontSize: "10px",
    color: "#6b7280",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "left",
  },
  counterError: {
    color: "#c62828",
    fontWeight: 700,
  },
  limitError: {
    margin: 0,
    fontSize: "12px",
    color: "#c62828",
    fontFamily: "objektiv-mk2, sans-serif",
  },
  saveError: {
    margin: "0 24px 12px",
    fontSize: "12px",
    color: "#c62828",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
  },
  dialogFooter: {
    padding: "16px 24px",
    background: "#f9fafb",
    borderTop: "1px solid #e5e7eb",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
    alignItems: "center",
  },
  footerLeft: {
    justifySelf: "start",
  },
  footerCenter: {
    justifySelf: "center",
  },
});

const CharCounter = ({ count, max, classes }) => {
  const overLimit = count > max;
  const atLimit = count >= max;
  return (
    <span
      className={`${classes.counter}${
        overLimit || atLimit ? ` ${classes.counterError}` : ""
      }`}
    >
      {count}/{max}
    </span>
  );
};

const DashboardHeaderSubtitleEditorModal = ({
  open,
  titleValue = "",
  subtitleValue = "",
  isDirty = false,
  onTitleChange,
  onSubtitleChange,
  onClose,
  onSave,
  saving = false,
  saveSuccess = false,
  saveError = null,
}) => {
  const classes = useStyles();
  const isLocked = saving || saveSuccess;
  const titleCount = titleValue ? titleValue.length : 0;
  const subtitleCount = subtitleValue ? subtitleValue.length : 0;
  const titleOverLimit = titleCount > MAX_TITLE_LENGTH;
  const subtitleOverLimit = subtitleCount > MAX_SUBTITLE_LENGTH;
  const overLimit = titleOverLimit || subtitleOverLimit;
  const saveLabel = saveSuccess
    ? "Saved"
    : saving
      ? "Saving..."
      : "Save Changes";

  return (
    <MailProDialog
      open={open}
      onClose={isLocked ? undefined : onClose}
      disableEscapeKeyDown={isLocked}
      classes={{ paper: classes.dialogPaper }}
    >
      <MailProDialogHeader>
        <div className={classes.dialogHeader}>
          <div className={classes.headerTitle}>Edit Dashboard Title & Subtitle</div>
        </div>
      </MailProDialogHeader>
      <MailProDialogBody>
        <div className={classes.dialogBody}>
          <div className={classes.fieldGroup}>
            <label className={classes.label} htmlFor="dashboard-header-title">
              Title
            </label>
            <input
              id="dashboard-header-title"
              type="text"
              className={`${classes.input}${
                titleOverLimit ? ` ${classes.fieldError}` : ""
              }`}
              value={titleValue}
              maxLength={MAX_TITLE_LENGTH}
              onChange={onTitleChange}
              disabled={isLocked}
            />
            <CharCounter
              count={titleCount}
              max={MAX_TITLE_LENGTH}
              classes={classes}
            />
            {titleOverLimit ? (
              <p className={classes.limitError} role="alert">
                Title cannot exceed {MAX_TITLE_LENGTH} characters. Shorten your
                text before saving.
              </p>
            ) : null}
          </div>

          <div className={classes.fieldGroup}>
            <label
              className={classes.label}
              htmlFor="dashboard-header-subtitle"
            >
              Subtitle
            </label>
            <textarea
              id="dashboard-header-subtitle"
              className={`${classes.textarea}${
                subtitleOverLimit ? ` ${classes.fieldError}` : ""
              }`}
              value={subtitleValue}
              maxLength={MAX_SUBTITLE_LENGTH}
              onChange={onSubtitleChange}
              disabled={isLocked}
              rows={4}
            />
            <CharCounter
              count={subtitleCount}
              max={MAX_SUBTITLE_LENGTH}
              classes={classes}
            />
            {subtitleOverLimit ? (
              <p className={classes.limitError} role="alert">
                Subtitle cannot exceed {MAX_SUBTITLE_LENGTH} characters. Shorten
                your text before saving.
              </p>
            ) : null}
          </div>
        </div>
      </MailProDialogBody>
      {saveError ? (
        <p className={classes.saveError} role="alert">
          {saveError}
        </p>
      ) : null}
      <div className={classes.dialogFooter}>
        <div className={classes.footerLeft}>
          <MailProButton
            variant="filled"
            label="Cancel"
            disabled={isLocked}
            onClick={onClose}
            customStyle={{
              fontSize: "12px",
              padding: "6px 14px",
              textTransform: "uppercase",
              fontWeight: "bold",
              minWidth: "auto",
              borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
              minHeight: DIALOG_BUTTON_MIN_HEIGHT,
              height: DIALOG_BUTTON_MIN_HEIGHT,
              boxSizing: "border-box",
            }}
          />
        </div>
        <div className={classes.footerCenter}>
          <MailProButton
            variant="filled"
            label={saveLabel}
            disabled={saving || overLimit || (!isDirty && !saveSuccess)}
            onClick={() =>
              onSave?.({
                title: titleValue || "",
                subtitle: subtitleValue || "",
              })
            }
            customStyle={{
              fontSize: "12px",
              padding: "8px 18px",
              textTransform: "uppercase",
              fontWeight: "bold",
              minWidth: "auto",
              borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
              minHeight: DIALOG_BUTTON_MIN_HEIGHT,
              height: DIALOG_BUTTON_MIN_HEIGHT,
              boxSizing: "border-box",
              ...(saveSuccess
                ? {
                    backgroundColor: "#2e7d32",
                    color: "#fff",
                    borderColor: "#2e7d32",
                    cursor: "default",
                  }
                : {}),
            }}
          />
        </div>
      </div>
    </MailProDialog>
  );
};

export default DashboardHeaderSubtitleEditorModal;
